import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { FiSend, FiMessageSquare } from 'react-icons/fi';
import { personName } from './ticketStatus';

// Own `tk-` class names on purpose: generic ones (.btn-primary, .comment-*) get restyled by other pages' CSS.

const idOf = (x) => String(x?._id || x?.id || x || '');
const shortTime = (date) => (date ? format(new Date(date), 'd MMM, h:mm a') : '');

function Avatar({ user, size = 32 }) {
  const style = { width: size, height: size };
  return user?.profilePicture
    ? <img src={user.profilePicture} alt="" className="tk-avatar" style={style} />
    : <span className="tk-avatar" style={style}>{user?.firstName?.[0]}{user?.lastName?.[0]}</span>;
}

// Chat-style comment thread with a composer at the bottom
export function CommentThread({ comments = [], myId, onSubmit, placeholder = 'Write a comment...' }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  // Grow with the text, up to ~5 lines
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [text]);

  const submit = async (e) => {
    e?.preventDefault();
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    const ok = await onSubmit(content);
    setSending(false);
    if (ok) setText('');
  };

  const handleKeyDown = (e) => {
    // Ctrl/Cmd+Enter sends; plain Enter keeps a newline (phones have no modifier keys)
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(e);
  };

  return (
    <section className="ticket-section area-comments">
      <h3><FiMessageSquare /> Comments {comments.length > 0 && <span className="tk-count">{comments.length}</span>}</h3>

      <div className="tk-thread">
        {comments.length === 0 && (
          <p className="tk-thread-empty">No comments yet. Start the conversation.</p>
        )}
        {comments.map((c, i) => {
          const own = idOf(c.author) === myId;
          const prev = comments[i - 1];
          const grouped = prev && idOf(prev.author) === idOf(c.author);
          return (
            <div key={c._id || i} className={`tk-msg ${own ? 'own' : ''} ${grouped ? 'grouped' : ''}`}>
              {!own && <div className="tk-msg-avatar">{!grouped && <Avatar user={c.author} size={30} />}</div>}
              <div className="tk-bubble">
                {!own && !grouped && <span className="tk-bubble-name">{personName(c.author)}</span>}
                <p>{c.content}</p>
                <span className="tk-bubble-time">{shortTime(c.createdAt)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <form className="tk-composer" onSubmit={submit}>
        <textarea
          ref={inputRef}
          rows={1}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
        <button type="submit" className="tk-send-btn" disabled={!text.trim() || sending} aria-label="Send comment">
          <FiSend />
        </button>
      </form>
    </section>
  );
}

const describe = (a) => {
  switch (a.action) {
    case 'created': return 'created this ticket';
    case 'assigned': return a.detail ? a.detail.replace(/^Assigned to/, 'assigned') : 'assigned the ticket';
    case 'unassigned': return a.detail ? a.detail.replace(/^Removed/, 'removed') : 'removed an assignee';
    case 'status_changed': return <>changed status <span className="tk-activity-chip">{a.detail}</span></>;
    case 'commented': return <>commented <span className="tk-activity-quote">“{a.detail}”</span></>;
    default: return `${a.action.replace(/_/g, ' ')}${a.detail ? ` — ${a.detail}` : ''}`;
  }
};

// Newest-first vertical timeline
export function ActivityTimeline({ activity = [], title = 'Activity' }) {
  const items = [...activity].reverse();
  return (
    <section className="ticket-section area-activity">
      <h3>{title}</h3>
      <ol className="tk-timeline">
        {items.map((a, i) => (
          <li key={a._id || i} className={`tk-timeline-item action-${a.action}`}>
            <span className="tk-timeline-dot" />
            <div className="tk-timeline-body">
              <p><strong>{personName(a.actor) || 'Someone'}</strong> {describe(a)}</p>
              <span className="tk-timeline-time">{shortTime(a.createdAt)}</span>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
